/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Row } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { Button, FieldGroup } from "core/components";

type PerformanceRateItemFormProps = {
  id: number;
  index: number;
  remove: (index: number) => void;
  isCostSavings: boolean;
  materialVarianceRateLength: number;
};

const PerformanceRateItemForm = ({ id, index, remove, isCostSavings, materialVarianceRateLength }: PerformanceRateItemFormProps): JSX.Element => {
  const { control, formState } = useFormContext();
  const fieldItemName = `performance_rate_items_attributes.[${index}]`;
  const errors = formState.errors?.performance_rate_items_attributes?.[index];

  const canRemove = () => {
    return !id;
  };

  return (
    <>
      {isCostSavings && materialVarianceRateLength === index && (
        <h5 className="mb-2">{I18n.t(`activerecord.attributes.performance_rate_item.rating_types.total_labor_variance_rate_35`)}</h5>
      )}
      <Row className="align-items-center">
        <Col>
          <Controller
            name={`${fieldItemName}.min`}
            control={control}
            render={({ field }) => (
              <FieldGroup label="Min" errorMessage={errors?.min?.message}>
                <div className="d-flex">
                  <IMaskInput
                    mask={Number}
                    padFractionalZeros={true}
                    thousandsSeparator=","
                    radix="."
                    value={field.value?.toString()}
                    unmask={true}
                    inputRef={field.ref}
                    onAccept={(value) => {
                      field.onChange(value);
                    }}
                    className="field"
                    placeholder="0.00"
                    signed
                    max={100}
                  />
                  <span
                    className="field"
                    style={{
                      flex: 1,
                      alignItems: "center",
                      fontWeight: "bold",
                      backgroundColor: "#e9ecef",
                      border: "1px solid #ced4da",
                      whiteSpace: "nowrap",
                      paddingTop: "10px",
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                  >
                    %
                  </span>
                </div>
              </FieldGroup>
            )}
          />
        </Col>
        <Col>
          <Controller
            name={`${fieldItemName}.max`}
            control={control}
            render={({ field }) => (
              <FieldGroup label="Max" errorMessage={errors?.max?.message}>
                <div className="d-flex">
                  <IMaskInput
                    mask={Number}
                    padFractionalZeros={true}
                    thousandsSeparator=","
                    radix="."
                    value={field.value?.toString()}
                    unmask={true}
                    inputRef={field.ref}
                    onAccept={(value) => {
                      field.onChange(value);
                    }}
                    className="field"
                    placeholder="0.00"
                    signed
                    max={100}
                  />
                  <span
                    className="field"
                    style={{
                      flex: 1,
                      alignItems: "center",
                      fontWeight: "bold",
                      backgroundColor: "#e9ecef",
                      border: "1px solid #ced4da",
                      whiteSpace: "nowrap",
                      paddingTop: "10px",
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                  >
                    %
                  </span>
                </div>
              </FieldGroup>
            )}
          />
        </Col>
        <Col>
          <Controller
            name={`${fieldItemName}.rating`}
            control={control}
            render={({ field }) => (
              <FieldGroup label="Rating" errorMessage={errors?.rating?.message}>
                <div className="d-flex">
                  <IMaskInput
                    mask={Number}
                    padFractionalZeros={true}
                    thousandsSeparator=","
                    radix="."
                    value={field.value?.toString()}
                    unmask={true}
                    inputRef={field.ref}
                    onAccept={(value) => {
                      field.onChange(value);
                    }}
                    className="field"
                    placeholder="0.00"
                    min={0}
                    max={100}
                  />
                  <span
                    className="field"
                    style={{
                      flex: 1,
                      alignItems: "center",
                      fontWeight: "bold",
                      backgroundColor: "#e9ecef",
                      border: "1px solid #ced4da",
                      whiteSpace: "nowrap",
                      paddingTop: "10px",
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                  >
                    %
                  </span>
                </div>
              </FieldGroup>
            )}
          />
        </Col>
        <Col sm={1}>
          <div className="d-flex justify-content-end align-content-end">
            {canRemove() && (
              <Button
                className="button--danger mr-2"
                icon="minus"
                onClick={() => {
                  remove(index);
                }}
              />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default PerformanceRateItemForm;
